/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core import
import { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import jsPDF from 'jspdf';
import * as html2image from 'html-to-image';
import { useTranslation } from 'react-i18next';
import ReactFlow, { Background, useEdgesState, useNodesState, useStore } from 'reactflow';

// Component imports
import { Button, Col, Divider, Grid, Modal, Row, Typography } from 'antd';
import { FileImageTwoTone, FilePdfTwoTone } from '@ant-design/icons';

// Misc imports
import { DEFAULT_NODE_HEIGHT, GRAPH_LAYOUTS, NODE_TYPES_COMPONENTS } from '../../constants/flowGraph';
import is from '../../utils/is';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Paragraph, Text, Title } = Typography;
const { useBreakpoint } = Grid;

// Util function to get the bounds, width and height of the graph
const getGraphBounds = (elements) => {
  let bounds = {
    minX: 0,
    maxX: 0,
    minY: 0,
    maxY: 0,
    width: 0,
    height: 0
  };

  if (elements && is.array(elements) && !is.emptyArray(elements)) {
    const nodes = elements.filter(element => element.data.nodeType === 'node');
    const nodesSortedByX = nodes.slice().sort((firstNode, secondNode) => firstNode.position.x - secondNode.position.x);
    const nodesSortedByY = nodes.slice().sort((firstNode, secondNode) => firstNode.position.y - secondNode.position.y);

    bounds = {
      minX: nodesSortedByX[0].position.x,
      maxX: nodesSortedByX[nodesSortedByX.length - 1].position.x,
      minY: nodesSortedByY[0].position.y,
      maxY: nodesSortedByY[nodesSortedByY.length - 1].position.y
    };

    bounds = {
      ...bounds,
      width: Math.abs(bounds.maxX - bounds.minX),
      height: Math.abs(bounds.maxY - bounds.minY)
    };
  }

  return bounds;
};

// Apologies for the timeouts 🥺
// There are page freezes due to the export mechanism and the timeouts are there to simulate loading
// Animated loaders will not work until the page freeze is sorted
const GraphExportModal = ({ onClose = () => {}, isModalOpen, baseClassName, elements, getLayoutedElements }) => {
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const isMobileSize = screens.xs;

  // React flow store
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const unselectNodesAndEdges = useStore((state) => state.unselectNodesAndEdges);

  const graphBounds = getGraphBounds([...nodes, ...edges]);

  const [reactFlowInstance, setReactFlowInstance] = useState();
  const [isGraphVisible, setIsGraphVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const exportOptions = {
    backgroundColor: 'white',
    quality: 5,
    pixelRatio: 2
  };

  const getCurrentElements = () => {
    return [...nodes, ...edges];
  };

  // When the conversation graph is fetched anew from the backend
  useEffect(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(elements.filter(el => el.data.nodeType === 'node'), elements.filter(el => el.data.nodeType === 'edge'), GRAPH_LAYOUTS.VERTICAL, true);
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
  }, []);

  // On modal open
  useEffect(() => {
    setIsLoading(true);

    if (isModalOpen) {
      setTimeout(() => {
        setIsGraphVisible(true);
        // Loading will be set to false in the graph onInit callback
      }, getCurrentElements() && getCurrentElements().length >= 100 ? 1000 : 500);
    } else {
      setIsGraphVisible(false);
      setIsLoading(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView();
      setIsLoading(false);
      unselectNodesAndEdges();
    }
  }, [reactFlowInstance]);

  const onExportClick = (format) => {
    switch (format) {
      case 'JPEG':
        setIsLoading(true);
        setTimeout(() => {
          html2image.toJpeg(document.querySelector('#FlowGraphToExport > .react-flow'), exportOptions).then((imageDataUrl) => {
            const a = document.createElement('a');

            a.setAttribute('download', 'ConversationFlowGraph.jpg');
            a.setAttribute('href', imageDataUrl);
            a.click();

            openConsoleAlertMessage({ type: 'success', message: t('Export successful! Your image will be downloaded soon.') });
            setIsLoading(false);
          });
        }, 200);
        break;
      case 'MultiPDF':
        setIsLoading(true);
        setTimeout(() => {
          html2image.toJpeg(document.querySelector('#FlowGraphToExport > .react-flow'), exportOptions).then((imageDataUrl) => {
            let imgWidth = 210;
            let pageHeight = 294;
            let imgHeight = ((graphBounds.height * imgWidth) / (graphBounds.width < 500 ? 500: graphBounds.width));
            var heightLeft = imgHeight;

            const imgData = imageDataUrl;

            const pdf = new jsPDF({
              orientation: 'p',
              unit: 'mm'
            });

            let position = 0;
            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
              heightLeft -= pageHeight;
            }
            pdf.save('ConversationFlowGraph.pdf');

            openConsoleAlertMessage({ type: 'success', message: t('Export successful! Your PDF will be downloaded soon.') });
            setIsLoading(false);
          });
        }, 200);
        break;
      default:
        break;
    }
  };

  return (
    <Fragment>
      <Modal
        title={t('Export conversation flow')}
        open={isModalOpen}
        onCancel={onClose}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
        footer={null}
        destroyOnClose
      >
        {
          getCurrentElements() ? (
            <Fragment>
              <div style={{ zIndex: 1 }}>
                <Row gutter={16} className="text-center">
                  <Col xs={24} className="mb-6">
                    <Title level={3}>
                      {t('Choose your preferred format')}
                    </Title>
                  </Col>

                  <Col
                    xs={24}
                    sm={12}
                    className={classNames('px-6', { 'border-right': !isMobileSize })}
                  >
                    <FileImageTwoTone className="fs-xxxxl" />

                    <Title level={3} className="mt-4 mb-1">
                      {t('JPEG')}
                    </Title>

                    <Paragraph>
                      {t('A single image that contains the entire flow.')}
                    </Paragraph>

                    <Button size="large" onClick={() => onExportClick('JPEG')} disabled={isLoading}>
                      {t('Export JPEG')}
                    </Button>
                  </Col>

                  {
                    isMobileSize ? (
                      <Divider />
                    ) : null
                  }

                  <Col xs={24} sm={12} className="px-6">
                    <FilePdfTwoTone className="fs-xxxxl" />

                    <Title level={3} className="mt-4 mb-1">
                      {t('PDF')}
                    </Title>

                    <Paragraph>
                      {t('A multiple page PDF file that contains the entire flow.')}
                    </Paragraph>

                    <Button size="large" onClick={() => onExportClick('MultiPDF')} disabled={isLoading}>
                      {t('Export PDF')}
                    </Button>
                  </Col>

                  <Col xs={24} style={{ height: 20 }} className="mt-5 mb-0">
                    {
                      isLoading ? (
                        <Text type="secondary">
                          {t('Please wait')}...
                        </Text>
                      ) : null
                    }
                  </Col>
                </Row>
              </div>

              <div className="w-100 overflow-scroll" style={{ height: 0 }}>
                <div
                  className={baseClassName}
                  id="FlowGraphToExport"
                  style={{
                    minWidth: '100%',
                    minHeight: DEFAULT_NODE_HEIGHT + 50,
                    width: graphBounds.width,
                    height: graphBounds.height,
                    opacity: 0
                  }}
                >
                  {
                    isGraphVisible && getCurrentElements() ? (
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={NODE_TYPES_COMPONENTS}
                        zoomOnScroll={false}
                        preventScrolling={false}
                        onInit={(reactFlowInstance) => setReactFlowInstance(reactFlowInstance)}
                        elementsSelectable={false}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        panOnDrag={false}
                      >
                        <Background variant="lines" gap={15} />
                      </ReactFlow>
                    ) : null
                  }
                </div>
              </div>
            </Fragment>
          ) : null
        }
      </Modal>
    </Fragment>
  );
};

export default GraphExportModal;

GraphExportModal.propTypes = {
  onClose: PropTypes.func,
  isModalOpen: PropTypes.bool,
  baseClassName: PropTypes.string,
  elements: PropTypes.array,
  getLayoutedElements: PropTypes.func
};
